import moment from 'moment';
import React, { useState, useEffect } from 'react';
import { Table, Button, Card, CardTitle, CardBody, CardText, Row, Col, UncontrolledCollapse } from 'reactstrap';
import Spinner from 'src/components/Spinner';
import { useNavigate } from 'react-router-dom';
import { useApp } from 'src/components/App/appContext';
import { useQuery } from '@tanstack/react-query';
import graphql from 'src/utils/graphql';
import EditProduct from 'src/components/EditProduct';
import styled from 'styled-components';
import WorkshopBooking from './WorkshopBooking';

const StyledTableContainer = styled.div`
	overflow-x: auto;
	@media (min-width: 768px) {
		overflow-x: visible;
	}
`;

const FilterContainer = styled.div`
	display: flex;
	justify-content: flex-end;
	margin-bottom: 1rem;
`;

export default function Servicing() {
	const navigate = useNavigate();
	const { user } = useApp();
	const [selectedStatus, setSelectedStatus] = useState('default');
	const [sortField, setSortField] = useState('date');
	const [sortDir, setSortDir] = useState('asc');

	const { data, isLoading, refetch } = useQuery(
		['myWorkshopBookings', selectedStatus],
		async () => {
			const data = await graphql(
				{
					query: `#graphql
				query ($statusIds: [bigint!]){
					workshopBooking(where: {statusId:{_in:$statusIds}} orderBy: {statusId: ASC, date: ASC}) {
						id
						person {
							name
						}
						equipmentType {
							name
						}
						equipmentMake
						equipmentModel
						date
						workshopBookingService {
    						product {
        						id
        						name
    						}
    					}
						status{
    						id
    						name
    					}
					}
					workshopBookingStatus {
						id
						name
  					}
					workshopService(orderBy:{listOrder:ASC}) {
						serviceId
						product {
							id
							description
							name
							price
							visible
							imageObject {
								url
							}
						}
					}
				}
			`,
					variables: {
						statusIds: selectedStatus === 'default' ? [1, 2, 3, 4, 6] : [selectedStatus]
					}
				},
				true
			);
			return { ...data };
		},
		{ enabled: !!user }
	);

	useEffect(() => {
		if (!user) return navigate('/');
	}, [user]);

	const sortedBookings = data?.workshopBooking ? [...data.workshopBooking].sort((a, b) => {
		let aVal, bVal;
		if (sortField === 'date') {
			aVal = a.date ? new Date(a.date) : (sortDir === 'asc' ? new Date('9999') : new Date(0));
			bVal = b.date ? new Date(b.date) : (sortDir === 'asc' ? new Date('9999') : new Date(0));
		} else if (sortField === 'status') {
			aVal = a.status?.name ?? '';
			bVal = b.status?.name ?? '';
		} else if (sortField === 'name') {
			aVal = a.person?.name ?? '';
			bVal = b.person?.name ?? '';
		} else if (sortField === 'id') {
			aVal = a.id;
			bVal = b.id;
		}
		if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
		if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
		return 0;
	}) : [];

	if (isLoading) return <Spinner />;

	return (
		<div>
			<h3 className="">Workshop Services</h3>
			<Row className="gx-3">
				{data.workshopService.map((s, i) => {
					if (!s.product) return null;
					if (!s.product.visible) return null;
					return (
						<Col key={s.product.id} md={3} lg={2}>
							<EditProduct id={s.product?.id} onSubmit={refetch}>
								<Card style={{ cursor: 'pointer' }}>
									<img src={s.product?.imageObject?.url || '/no-image.png'} alt={s.product.name} />
									<CardBody>
										<CardTitle>
											{s.product.name}
											<br />
											<b>£{s.product.price}</b>
										</CardTitle>

										<CardText>{s.product.description}</CardText>
									</CardBody>
								</Card>
							</EditProduct>
						</Col>
					);
				})}
			</Row>
			{data.workshopService.filter((s) => s.product).some((s) => !s.product.visible) && (
				<>
					<Button outline className="mt-2" color="primary" id="toggler" style={{ marginBottom: '1rem' }}>
						Toggle Hidden Services
					</Button>
					<UncontrolledCollapse toggler="#toggler">
						<Row className="gx-3">
							{data.workshopService.map((s, i) => {
								if (!s.product) return null;
								if (s.product.visible) return null;
								return (
									<Col key={s.product.id} md={3} lg={2}>
										<EditProduct id={s.product?.id} onSubmit={refetch}>
											<Card style={{ cursor: 'pointer' }}>
												<img src={s.product?.imageObject?.url || '/no-image.png'} alt={s.product.name} />
												<CardBody>
													<CardTitle>{s.product.name}</CardTitle>
													<CardText>{s.product.description}</CardText>
												</CardBody>
											</Card>
										</EditProduct>
									</Col>
								);
							})}
						</Row>
					</UncontrolledCollapse>
				</>
			)}
			<h3 className="mt-5">Workshop Bookings</h3>
			<FilterContainer style={{ gap: '1rem' }}>
				<select value={selectedStatus} onChange={(e) => setSelectedStatus(e.target.value)}>
					<option value="default">All Except Collected</option>
					{data.workshopBookingStatus.map((status) => (
						<option key={status.id} value={status.id}>
							{status.name}
						</option>
					))}
				</select>
				<select value={sortField} onChange={(e) => setSortField(e.target.value)}>
					<option value="date">Sort by Drop Off Date</option>
					<option value="status">Sort by Status</option>
					<option value="name">Sort by Client Name</option>
					<option value="id">Sort by Booking ID</option>
				</select>
				<select value={sortDir} onChange={(e) => setSortDir(e.target.value)}>
					<option value="asc">Ascending</option>
					<option value="desc">Descending</option>
				</select>
			</FilterContainer>
			<StyledTableContainer>
				<Table hover className="mt-2">
					<thead>
						<tr>
							<th>Booking ID</th>
							<th>Client Name</th>
							<th>Service Booked</th>
							<th>Booking Status</th>
							<th>Drop Off Date</th>
							<th></th>
						</tr>
					</thead>
					<tbody>
					{sortedBookings.map((b, i) => {
								return (
									<tr key={i}>
										<td>{b.id}</td>
										<td>{b.person.name}</td>
										<td>{b.workshopBookingService?.product.name}</td>
										<td>{b.status?.name}</td>
										<td>{b.date ? moment(b.date).format('DD/MM/YYYY') : 'Not Booked'}</td>
										<td>
											<Button color="primary" onClick={() => navigate(`/admin/workshop/` + b.id)}>
												View
											</Button>
										</td>
									</tr>
								);
							})}
					</tbody>
				</Table>
			</StyledTableContainer>
		</div>
	);
}
